import React, { useState, useEffect } from "react"; // core React + hooks
import { Listbox, ListboxButton, ListboxOptions, ListboxOption, Portal } from "@headlessui/react";
import { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";

// import { Swiper, SwiperSlide } from "swiper/react";
// import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

interface GuestDetailsProps {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  availableClassNames: string[];
  classDurationMap: Record<string, string>;
  guestOptions: number[];
  occOptions: Record<number, string[]>;
  guestCount: number;
  // unusedRooms: number;
  defaultClassName: string;
  onClassChange: (newClassName: string) => void;
  isFixedMode: boolean;
  isGlobalSearch: boolean;
  selectedWindow: any; // 👈 new
  availableDurations: string[]; // 👈 new
  onResetFilters?: () => void;
  isRoomMode?: boolean;
  isDesktop?: boolean;
  isSpecificFixedClassPage?: boolean; // 👈 new
  durationLabels?: Record<string, string>; // 👈 new
}

export default function GuestDetails({
  register,
  setValue,
  watch,
  availableClassNames,
  classDurationMap,
  guestOptions,
  occOptions,
  guestCount,
  // unusedRooms,
  defaultClassName,
  onClassChange,
  isFixedMode,
  isGlobalSearch,
  selectedWindow,
  availableDurations,
  onResetFilters,
  isRoomMode = false,
  isDesktop = false,
  isSpecificFixedClassPage = false,
  durationLabels = {},
}: GuestDetailsProps) {
  const selectedOcc = watch("Occupancy");
  // console.log("&&&***&&&Class name selection updated&&&***&&&:", watch("ExperienceTitle"));
  const rawClassName = watch("ExperienceTitle");
  // console.log("🔍 GuestDetails dropdown trace:", {
  //   rawClassName,
  //   defaultClassName,
  //   selectedClassNameBefore: !rawClassName || rawClassName.trim() === "" ? defaultClassName ?? availableClassNames[0] : rawClassName,
  //   availableClassNames,
  // });

  // "" should be respected as “All Classes”
  const selectedClassName =
    rawClassName !== undefined
      ? rawClassName // includes "" sentinel
      : (defaultClassName ?? availableClassNames[0]);

  const selectedClassLength = watch("Experience");

  const threeDayClassNames = availableClassNames.filter((name) => classDurationMap?.[name] === "0");

  const fiveDayClassNames = availableClassNames.filter((name) => classDurationMap?.[name] === "1");

  useEffect(() => {
    if (isRoomMode) return;
    // current title from form
    const currentTitle = watch("ExperienceTitle");

    // If there are no classes for this mode/length, do nothing
    if (!availableClassNames || availableClassNames.length === 0) return;

    // If current title is valid, keep it
    // 🔥 Do NOT override All Classes
    if (currentTitle === "All Classes") return;

    if (currentTitle && availableClassNames.includes(currentTitle)) return;

    setValue("ExperienceTitle", availableClassNames[0], {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [availableClassNames, watch, setValue]);

  // Helpers to format occupancy labels
  const formatCompact = (label: string) =>
    label
      .replace(/(\d+)\s+Single Room[s]?/g, "$1 × Single")
      .replace(/(\d+)\s+Double Room[s]?/g, "$1 × Double")
      .replace(/\s*\/\s*/g, " + ");

  const formatTooltip = (label: string) => label.replace(/\//g, " and ");

  const renderClassLengthSelect = () => (
    <select
      id="Experience"
      className="text-input w-full"
      value={watch("Experience")}
      onChange={(e) => {
        const newLen = e.target.value;
        setValue("Experience", newLen, { shouldValidate: true, shouldDirty: true });
      }}
    >
      <option value="">All Class Lengths</option>
      <option value="">{durationLabels?.all ?? "All Class Lengths"}</option>
      {availableDurations.includes("0") && <option value="0">{durationLabels?.["0"] ?? "3-Day (4 nights)"}</option>}
      {availableDurations.includes("1") && <option value="1">{durationLabels?.["1"] ?? "5-Day (6 nights)"}</option>}
    </select>
  );

  const renderClassNameSelect = () => (
    <select
      id="ExperienceTitle"
      className="text-input w-full"
      value={selectedClassName}
      onChange={(e) => {
        const newClass = e.target.value;
        if (newClass === "All Classes") setSelectedWindow?.(null);
        setValue("ExperienceTitle", newClass, { shouldValidate: true, shouldDirty: true });
        onClassChange?.(newClass);
      }}
    >
      {isFixedMode && !isSpecificFixedClassPage && <option value="">All Classes</option>}
      {selectedClassLength === "" ? (
        <>
          {threeDayClassNames.length > 0 && (
            <optgroup label="3-Day Classes">
              {threeDayClassNames.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </optgroup>
          )}

          {fiveDayClassNames.length > 0 && (
            <optgroup label="5-Day Classes">
              {fiveDayClassNames.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </optgroup>
          )}
        </>
      ) : (
        availableClassNames.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))
      )}
    </select>
  );

  const mobile = !isDesktop;

  return (
    <div className="flex flex-col px-2 mt-0" style={{ width: "var(--guest-inner-width)", gap: "var(--input-row-gap)" }}>
      {/* ROOM MODE */}
      {isRoomMode ? (
        // Room mode UI (same for mobile & desktop) — single source of truth
        <div className="text-sm md:text-md">
          <div className="flex flex-row items-end space-x-3 space-y-1.5">
            <div className="flex flex-col flex-1 space-y-1">
              <label htmlFor="ExperienceTitle" className="text-left font-semibold text-gray-700 leading-tight">
                Suite Name
              </label>
              <div className="w-full bg-white border-2 rounded-[3px] border-gray-300 px-1.5 text-gray-900">{selectedClassName}</div>
            </div>

            <div className="flex flex-col w-24 space-y-1">
              <label htmlFor="NumberofGuests" className="text-left font-semibold text-gray-700 leading-tight">
                # Guests
              </label>
              <select id="NumberofGuests" className="text-input w-full" {...register("NumberofGuests", { required: true })}>
                {guestOptions.map((num) => (
                  <option key={num} value={String(num)}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ) : (
        // CLASS MODE
        <>
          {/* Class Length + Class Name: always render (single source) */}
          <div className={mobile ? "flex gap-2 text-sm" : "hidden"} style={{ display: mobile ? undefined : "none" }}>
            {/* mobile class length + name */}
            <div className="flex flex-col w-[42%] space-y-1">
              <label htmlFor="Experience" className="text-sm font-semibold text-gray-700">
                Class Length
              </label>
              {renderClassLengthSelect()}
            </div>
            <div className="flex flex-col flex-1 space-y-1">
              <label htmlFor="ExperienceTitle" className="text-sm font-semibold text-gray-700">
                Class Name
              </label>
              {renderClassNameSelect()}
            </div>
          </div>

          <div style={{ display: mobile ? "none" : undefined }}>
            {/* desktop class length + name */}
            {isDesktop && (
              <div className="flex flex-col space-y-4">
                <div className="flex flex-col space-y-1">
                  <label htmlFor="Experience" className="text-sm font-semibold text-gray-700">
                    Class Length
                  </label>
                  {renderClassLengthSelect()}
                </div>
                <div className="flex flex-col space-y-1">
                  <label htmlFor="ExperienceTitle" className="text-sm font-semibold text-gray-700">
                    Class Name
                  </label>
                  {renderClassNameSelect()}
                </div>
              </div>
            )}
          </div>

          {/* GUESTS + OCCUPANCY */}
          {/* MOBILE: render only when mobile === true */}
          {mobile && (
            <div className="flex gap-x-4 mb-1">
              <div className="w-1/3 flex flex-col">
                <label htmlFor="NumberofGuests" className="text-sm text-left font-semibold text-gray-700">
                  # Guests
                </label>
                <select
                  id="NumberofGuests"
                  className="text-input text-sm w-full"
                  {...register("NumberofGuests", { required: true })}
                  disabled={guestOptions.length === 0}
                >
                  {guestOptions.map((num) => (
                    <option key={num} value={String(num)}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-2/3 flex flex-col">
                <label htmlFor="Occupancy" className="text-sm text-left font-semibold text-gray-700">
                  Occupancy
                </label>
                <select
                  id="Occupancy"
                  className="text-input text-sm w-full"
                  value={selectedOcc}
                  onChange={(e) =>
                    setValue("Occupancy", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  disabled={!(occOptions[guestCount] && occOptions[guestCount].length)}
                >
                  {(occOptions[guestCount] || []).map((fullLabel, i) => (
                    <option key={i} value={fullLabel}>
                      {formatCompact(fullLabel)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* DESKTOP: render only when isDesktop === true */}
          {isDesktop && (
            <div className="flex flex-col mb-1">
              <label htmlFor="NumberofGuests" className="text-sm text-left font-semibold text-gray-700">
                # Guests
              </label>
              <select
                id="NumberofGuests"
                className="text-input w-full"
                {...register("NumberofGuests", { required: true })}
                disabled={guestOptions.length === 0}
              >
                {guestOptions.map((num) => (
                  <option key={num} value={String(num)}>
                    {num}
                  </option>
                ))}
              </select>

              <label htmlFor="Occupancy" className="text-sm text-left font-semibold text-gray-700 mt-3">
                Occupancy
              </label>
              <select
                id="Occupancy"
                className="text-input w-full"
                value={selectedOcc}
                onChange={(e) =>
                  setValue("Occupancy", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                disabled={!(occOptions[guestCount] && occOptions[guestCount].length)}
              >
                {(occOptions[guestCount] || []).map((fullLabel, i) => (
                  <option key={i} value={fullLabel}>
                    {formatCompact(fullLabel)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
      {/* Footer controls: Private + Reset */}
      <div className="flex items-center justify-between py-0" style={{ marginBottom: "var(--input-bottom-gap" }}>
        {/* Private Experience */}
        {!isRoomMode && !isFixedMode && !isGlobalSearch && guestCount === 2 && (
          <div className="flex items-center gap-2 relative">
            <input
              id="PrivateExperience"
              type="checkbox"
              {...register("PrivateExperience")}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label htmlFor="PrivateExperience" className="relative inline-block text-xs font-semibold text-gray-700 group">
              Private Experience
              <span className="pointer-events-none absolute left-full top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-black text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 delay-[150ms]">
                Click to ensure that your party has an exclusive 2 person session — a surcharge applies
              </span>
            </label>
          </div>
        )}

        {/* Reset Filters */}
        {!isRoomMode && (isFixedMode || isGlobalSearch) && onResetFilters && (
          <div className="relative group">
            <button
              type="button"
              onClick={onResetFilters}
              className="px-3 py-1 text-xs font-semibold rounded"
              style={{
                backgroundColor: "#d8bd80",
                color: "white",
                border: "none",
              }}
            >
              Reset All Filters
            </button>
            <span className="absolute left-full top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-black text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition">
              Reset filters to “All Classes / All Lengths”
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
